import { ItemDetailTabKey } from '@/enums';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useState } from 'react';

type OrderEntryRouteProp = RouteProp<
  SalesPortalStackParams,
  SALES_PORTAL_ROUTES.OrderEntry
>;

/**
 * Imperative API + reactive state for the order entry item-detail side panel.
 * The currently visible variant pack and the active tab are encoded on the
 * route's query params (`variantPackUUID`, `itemDetailTab`) so the panel can
 * be deep-linked. The drill-down history beneath the visible pack lives in
 * component state only — refreshing the page resets the back arrow, which is
 * acceptable because users land on a specific pack and drill down from there
 * rather than sharing mid-drill-down URLs.
 */
export interface OrderEntryItemDetailsNavigation {
  /** Currently selected tab; defaults to `General` when no param is set. */
  activeTab: ItemDetailTabKey;
  /** Close the panel and clear the tab + drill-down state. */
  closePanel: () => void;
  /**
   * Variant pack UUIDs the panel was previously open on beneath the currently
   * visible pack, oldest first. Empty when the panel is at the bottom of the
   * drill-down stack (or closed).
   */
  navigationStack: string[];
  /**
   * Open the panel at the given variant pack, clearing any drill-down history.
   * The currently active tab is preserved across `openPanel` calls so reps
   * who were comparing on Details stay on Details when clicking the next
   * item; the tab only resets to General after `closePanel` (which clears
   * `itemDetailTab` so the next open falls back to the default).
   */
  openPanel: (variantPackUUID: string) => void;
  /**
   * Pop the most recent entry off the drill-down stack and make it the
   * currently visible variant pack. No-op when the stack is empty. The
   * active tab is preserved.
   */
  popVariantPack: () => void;
  /**
   * Push a new variant pack onto the drill-down stack — the currently visible
   * pack moves into the underlying history and `variantPackUUID` becomes
   * the new pack. No-op when the panel is closed. The active tab resets to
   * General so drilling into a pack lands on its overview rather than the
   * pack's own related-packs list.
   */
  pushVariantPack: (variantPackUUID: string) => void;
  /** Currently displayed variant pack; null when the panel is closed. */
  selectedVariantPackUUID: string | null;
  /** Set the active tab inside the panel. */
  setActiveTab: (tab: ItemDetailTabKey) => void;
}

/**
 * Reads/writes the panel's visible variant pack + active tab from the order
 * entry route (`variantPackUUID`, `itemDetailTab`). The drill-down stack
 * beneath the visible pack is held in component state only — see the type
 * docs for the rationale.
 *
 * @returns the panel navigation state and imperative actions.
 */
export const useOrderEntryItemDetailsNavigation =
  (): OrderEntryItemDetailsNavigation => {
    const route = useRoute<OrderEntryRouteProp>();
    const navigation =
      useNavigation<StackNavigationProp<SalesPortalStackParams>>();

    const selectedVariantPackUUID = route.params?.variantPackUUID ?? null;
    const activeTab = route.params?.itemDetailTab ?? ItemDetailTabKey.General;
    const [navigationStack, setNavigationStack] = useState<string[]>([]);

    const openPanel = useCallback(
      (variantPackUUID: string) => {
        setNavigationStack([]);
        navigation.setParams({ variantPackUUID });
      },
      [navigation],
    );

    const closePanel = useCallback(() => {
      setNavigationStack([]);
      navigation.setParams({
        itemDetailTab: undefined,
        variantPackUUID: undefined,
      });
    }, [navigation]);

    const setActiveTab = useCallback(
      (tab: ItemDetailTabKey) => {
        navigation.setParams({ itemDetailTab: tab });
      },
      [navigation],
    );

    const pushVariantPack = useCallback(
      (variantPackUUID: string) => {
        if (!selectedVariantPackUUID) {
          return;
        }
        setNavigationStack((prev) => [...prev, selectedVariantPackUUID]);
        navigation.setParams({
          itemDetailTab: ItemDetailTabKey.General,
          variantPackUUID,
        });
      },
      [navigation, selectedVariantPackUUID],
    );

    const popVariantPack = useCallback(() => {
      if (navigationStack.length === 0) {
        return;
      }
      const previousVariantPackUUID =
        navigationStack[navigationStack.length - 1];
      setNavigationStack((prev) => prev.slice(0, -1));
      navigation.setParams({ variantPackUUID: previousVariantPackUUID });
    }, [navigation, navigationStack]);

    return {
      activeTab,
      closePanel,
      navigationStack,
      openPanel,
      popVariantPack,
      pushVariantPack,
      selectedVariantPackUUID,
      setActiveTab,
    };
  };
